import styled, { keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const countUp = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to   { opacity: 1; transform: scale(1); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.6; }
  50%       { transform: scale(1.18); opacity: 1; }
`;

// ── Page shell ─────────────────────────────────────────────────────────────────
export const DashPage = styled.div`
  padding: 60px 0 80px;

  @media (max-width: 620px) {
    padding: 40px 0 60px;
  }
`;

// ── Header ─────────────────────────────────────────────────────────────────────
export const DashHeader = styled.div`
  text-align: center;
  margin-bottom: 36px;
  animation: ${fadeUp} 0.4s ease both;
`;

export const DashTitle = styled.h2`
  font-size: 42px;
  font-family: rebondG-Bold;
  color: #0f172a;
  margin: 0 0 10px;

  @media (max-width: 620px) {
    font-size: 32px;
  }
`;

export const DashSubtitle = styled.p`
  font-size: 20px;
  color: #64748b;
  margin: 0;
  font-family: rebondG-Medium;
`;

// ── Cards grid ─────────────────────────────────────────────────────────────────
export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

// Last card spans full width when total is 5 (odd number)
export const StatCardWrap = styled.div`
  animation: ${fadeUp} 0.45s ease both;
  animation-delay: ${({ $idx }) => $idx * 80}ms;

  &:nth-child(5):last-child {
    @media (min-width: 900px) {
      /* center the 5th card in 3-col grid */
      grid-column: 2 / 3;
    }

    @media (max-width: 900px) and (min-width: 481px) {
      grid-column: 1 / -1;
      max-width: 360px;
      margin: 0 auto;
      width: 100%;
    }
  }
`;

export const StatCard = styled.div`
  position: relative;
  background: #fff;
  border-radius: 20px;
  padding: 28px 24px 24px;
  border: 1px solid #f1f5f9;
  overflow: hidden;
  cursor: default;
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  box-shadow:
    0 1px 3px rgba(0,0,0,0.04),
    0 6px 20px rgba(0,0,0,0.06);

  &:hover {
    transform: translateY(-4px);
    box-shadow:
      0 4px 8px rgba(0,0,0,0.06),
      0 16px 40px rgba(0,0,0,0.10);
  }

  /* Top accent bar */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ $color }) => $color || '#2f67d8'};
    border-radius: 20px 20px 0 0;
  }

  @media (max-width: 480px) {
    padding: 20px 16px 18px;
    border-radius: 16px;
  }
`;

// Decorative glow blob behind icon
export const CardGlow = styled.div`
  position: absolute;
  top: -20px;
  right: -20px;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: ${({ $color }) => $color || '#2f67d8'};
  opacity: 0.06;
  pointer-events: none;
`;

export const CardTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
`;

export const CardIconWrap = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  background: ${({ $color }) => $color ? `${$color}15` : 'rgba(47,103,216,0.08)'};
  color: ${({ $color }) => $color || '#2f67d8'};

  svg {
    animation: ${pulse} 3s ease-in-out infinite;
  }

  @media (max-width: 480px) {
    width: 38px;
    height: 38px;
    font-size: 16px;
    border-radius: 10px;
  }
`;

export const CardTrendBadge = styled.div`
  font-size: 11px;
  font-family: rebondG-Semi-Bold;
  padding: 3px 8px;
  border-radius: 20px;
  background: rgba(16,185,129,0.1);
  color: #059669;
`;

export const CardValue = styled.div`
  font-size: 64px;
  font-family: rebondG-Bold;
  color: #0f172a;
  line-height: 1;
  margin-bottom: 12px;
  letter-spacing: -1.5px;
  animation: ${countUp} 0.5s cubic-bezier(0.34,1.56,0.64,1) both;
  animation-delay: ${({ $delay }) => $delay || 0}ms;

  @media (max-width: 480px) {
    font-size: 50px;
    letter-spacing: -1px;
  }
`;

export const CardLabel = styled.p`
  font-size: 23px;
  color: #1e293b;
  margin: 0;
  line-height: 1.3;
  font-family: rebondG-Semi-Bold;

  @media (max-width: 480px) {
    font-size: 19px;
  }
`;

export const CardDivider = styled.div`
  height: 1px;
  background: #f1f5f9;
  margin: 16px 0 12px;
`;

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 17px;
  color: #64748b;
  font-family: rebondG-Medium;
`;

export const CardFooterDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color || '#2f67d8'};
  flex-shrink: 0;
`;
